import React, { useEffect, useMemo, useState } from 'react';
import { getLeaderboard, type LeaderboardEntry } from '../api/gameApi';

interface LeaderboardScreenProps {
  onBack: () => void;
}

function formatDaysAgo(endedAt: string | null): string {
  if (!endedAt) return 'No completion date';
  const endedDate = new Date(endedAt);
  if (Number.isNaN(endedDate.getTime())) return 'Unknown date';

  const now = Date.now();
  const diffMs = now - endedDate.getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  const days = Math.max(0, Math.floor(diffMs / dayMs));

  if (days === 0) return 'Today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

function rankStyles(rank: number): string {
  if (rank === 1) return 'bg-gradient-to-br from-amber-300 to-amber-500 text-[#3e2b00]';
  if (rank === 2) return 'bg-gradient-to-br from-slate-200 to-slate-400 text-[#1b253a]';
  if (rank === 3) return 'bg-gradient-to-br from-orange-300 to-orange-500 text-[#3b1d00]';
  return 'bg-[#27315D] text-cyan-100';
}

const LeaderboardScreen: React.FC<LeaderboardScreenProps> = ({ onBack }) => {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadLeaderboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getLeaderboard(10, 'last_6_months', 'score');
      setEntries(data.results ?? []);
    } catch (err) {
      console.error('Failed to load leaderboard:', err);
      setError('Could not load leaderboard right now.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadLeaderboard();
  }, []);

  const sortedEntries = useMemo(
    () => [...entries].sort((a, b) => b.score - a.score),
    [entries],
  );

  return (
    <div
      className="relative min-h-screen flex items-center justify-center lg:justify-end bg-cover bg-center overflow-hidden px-4 lg:pr-4 lg:pl-28"
      
      style={{ backgroundImage: "url('/backgrounds/game-landing-bg.svg')" }}
    >
      <div className="mx-auto max-w-4xl h-[calc(100vh-120px)] rounded-2xl border border-[#2E3A6B] bg-[#1b2a41]/90 p-6 sm:p-8 shadow-2xl flex flex-col font-caudex">
        <div className="flex items-center justify-between mb-6 gap-3">
          <h1
            style={{
              color: '#A5FFF5',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 'clamp(26px, 3vw, 36px)',
              fontWeight: 400,
            }}
          >
            Leaderboard
          </h1>
          <button
            onClick={onBack}
            className="px-4 py-2 rounded-full border border-white/30 hover:bg-white/10 transition text-white text-sm font-semibold font-caudex"
          >
            Back
          </button>
        </div>

        <p className="text-white/75 mb-6 text-sm sm:text-base">
          Ranked by final score from completed full runs.
        </p>
        {loading ? (
          <div className="rounded-xl border border-white/15 bg-[#0d1b3e]/70 p-6 text-center text-white/80">
            Loading leaderboard...
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-400/30 bg-[#0d1b3e]/70 p-6 text-center text-white/90">
            <p className="mb-4">{error}</p>
            <button
              onClick={() => void loadLeaderboard()}
              className="px-4 py-2 rounded-full border border-white/40 hover:bg-white/10 transition text-sm font-semibold text-white font-caudex"
            >
              Retry
            </button>
          </div>
        ) : sortedEntries.length === 0 ? (
          <div className="rounded-xl border border-white/15 bg-[#0d1b3e]/70 p-6 text-center text-white/80">
            No completed full runs yet.
          </div>
        ) : (
          <div className="space-y-3 overflow-y-auto pr-1 flex-1 min-h-0">
            <div className="hidden sm:grid grid-cols-[88px_1fr_170px_150px] px-3 text-xs uppercase tracking-wide text-white/60">
              <span>Rank</span>
              <span>Name</span>
              <span>Score</span>
              <span>Date Achieved</span>
            </div>
            {sortedEntries.map((item, index) => (
              <article
                key={item.session_id}
                className="rounded-2xl border border-[#4C5A9B] bg-gradient-to-r from-[#1e2a58]/95 to-[#243064]/95 p-3 sm:p-4 shadow-[0_0_0_1px_rgba(255,255,255,0.04),0_10px_30px_rgba(0,0,0,0.35)]"
              >
                <div className="grid grid-cols-1 sm:grid-cols-[88px_1fr_170px_150px] gap-3 sm:gap-2 items-center">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${rankStyles(index + 1)}`}>
                      #{index + 1}
                    </div>
                    <span className="sm:hidden text-xs text-white/60">Rank</span>
                  </div>

                  <div className="rounded-xl border border-white/15 bg-[#0f1b45]/60 px-3 py-2">
                    <p className="sm:hidden text-xs text-white/60 mb-0.5">Name</p>
                    <p className="text-white font-semibold truncate">{item.display_name || 'Unknown'}</p>
                  </div>

                  <div className="rounded-xl border border-cyan-200/25 bg-cyan-300/10 px-3 py-2">
                    <p className="sm:hidden text-xs text-white/60 mb-0.5">Score</p>
                    <p className="text-cyan-100 font-bold text-lg leading-tight">{Math.floor(item.score)}</p>
                  </div>

                  <div className="rounded-xl border border-white/15 bg-white/5 px-3 py-2">
                    <p className="sm:hidden text-xs text-white/60 mb-0.5">Date Achieved</p>
                    <p className="text-white font-semibold">{formatDaysAgo(item.ended_at)}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaderboardScreen;
